"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import clsx from "clsx";
import { useActiveSectionContext } from "@/context/active-section-context";
import { useI18nContext } from "@/context/i18n-context";
import { SectionName } from "@/lib/types";

type NavLink = { name: string; hash: string };

const LANG_OPTIONS = [
  { code: "en", label: "EN" },
  { code: "zh", label: "中文" },
  { code: "es", label: "ES" },
] as const;

function LanguageSwitcher() {
  const { language, changeLanguage } = useI18nContext();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = LANG_OPTIONS.find((l) => l.code === language) ?? LANG_OPTIONS[0];

  return (
    <>
      {/* ── Segmented control — visible on sm+ ── */}
      <div
        className="hidden sm:flex items-center"
        role="group"
        aria-label="Select language"
      >
        {/* Thin separator */}
        <span
          className="w-px h-4 mx-3 rounded-full"
          style={{ background: "var(--color-border)" }}
          aria-hidden
        />
        <div
          className="flex items-center gap-0.5 rounded-full p-0.5"
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-subtle)",
          }}
        >
          {LANG_OPTIONS.map(({ code, label }) => {
            const active = language === code;
            return (
              <button
                key={code}
                onClick={() => changeLanguage(code)}
                aria-pressed={active}
                aria-label={`Switch to ${label}`}
                className={clsx(
                  "relative px-2.5 py-1 rounded-full text-[0.72rem] font-semibold tracking-wide transition-colors duration-150",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
                  active
                    ? "text-[var(--color-text-primary)]"
                    : "text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]"
                )}
                style={{ letterSpacing: "0.03em" }}
              >
                {active && (
                  <motion.span
                    layoutId="langActiveIndicator"
                    className="absolute inset-0 rounded-full -z-10"
                    style={{ background: "var(--color-brand-subtle)" }}
                    transition={{ type: "spring", stiffness: 500, damping: 38 }}
                  />
                )}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Dropdown — visible on mobile only ── */}
      <div
        className="sm:hidden relative flex items-center"
        ref={dropdownRef}
      >
        {/* Thin separator */}
        <span
          className="w-px h-4 mx-2 rounded-full"
          style={{ background: "var(--color-border)" }}
          aria-hidden
        />
        <button
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Select language"
          className={clsx(
            "flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.72rem] font-semibold tracking-wide transition-colors duration-150",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
            "text-[var(--color-text-secondary)]"
          )}
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-subtle)",
            letterSpacing: "0.03em",
          }}
        >
          {current.label}
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            aria-hidden
            className="transition-transform duration-150"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <path
              d="M2 3.5L5 6.5L8 3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <AnimatePresence>
          {open && (
            <motion.ul
              role="listbox"
              aria-label="Language options"
              initial={{ opacity: 0, y: -6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.96 }}
              transition={{ duration: 0.15, ease: [0.25, 1, 0.5, 1] }}
              className="absolute right-0 top-full mt-2 z-50 rounded-xl overflow-hidden"
              style={{
                background: "var(--color-surface-raised)",
                border: "1px solid var(--color-border)",
                boxShadow: "0 4px 16px oklch(0% 0 0 / 0.12)",
                minWidth: "6rem",
              }}
            >
              {LANG_OPTIONS.map(({ code, label }) => {
                const active = language === code;
                return (
                  <li key={code} role="option" aria-selected={active}>
                    <button
                      onClick={() => {
                        changeLanguage(code);
                        setOpen(false);
                      }}
                      className={clsx(
                        "w-full text-left px-4 py-2 text-[0.8rem] font-medium transition-colors duration-100",
                        "focus-visible:outline-none",
                        active
                          ? "text-[var(--color-brand)]"
                          : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
                      )}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export default function Header() {
  const { language, t } = useI18nContext();
  const { activeSection, setActiveSection, setTimeOfLastClick } =
    useActiveSectionContext();
  const [links, setLinks] = useState<NavLink[]>(t("links"));
  // Keep a ref to the previous link list so we can re-translate activeSection
  const prevLinksRef = useRef<NavLink[]>(t("links"));

  useEffect(() => {
    const newLinks: NavLink[] = t("links");
    // Re-map activeSection: find its index in prev list, use same index in new list
    const prevIdx = prevLinksRef.current.findIndex((l) => l.name === activeSection);
    if (prevIdx !== -1 && newLinks[prevIdx]) {
      setActiveSection(newLinks[prevIdx].name);
    }
    prevLinksRef.current = newLinks;
    setLinks(newLinks);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  return (
    <header className="z-[999] relative sm:flex sm:justify-center">
      <motion.div
        className="fixed top-0 left-0 w-full sm:left-auto sm:top-5 sm:w-auto
          bg-[var(--color-surface-raised)]/90
          backdrop-blur-md
          border-b border-[var(--color-border-subtle)]
          sm:border sm:rounded-full
          shadow-sm"
        initial={{ y: -64, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
      >
        <nav className="flex w-full sm:w-auto items-center justify-between sm:justify-center gap-1
          min-h-14 py-2 sm:py-0 sm:h-12 px-3 sm:px-4">
          <ul className="flex flex-wrap items-center justify-start sm:justify-center gap-x-0.5 gap-y-1
            min-w-0 flex-1 sm:flex-none
            text-[0.82rem] font-medium text-[var(--color-text-tertiary)]">
            {links.map((link) => (
              <li key={link.hash} className="relative flex items-center">
                <Link
                  href={link.hash}
                  className={clsx(
                    "relative px-3 py-2 rounded-full transition-colors duration-150",
                    "hover:text-[var(--color-text-primary)]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
                    {
                      "text-[var(--color-text-primary)]":
                        activeSection === link.name,
                      "text-[var(--color-text-tertiary)]":
                        activeSection !== link.name,
                    }
                  )}
                  onClick={() => {
                    setTimeOfLastClick(Date.now());
                    setActiveSection(link.name as SectionName);
                  }}
                >
                  {link.name}
                  {link.name === activeSection && (
                    <motion.span
                      className="absolute inset-0 rounded-full -z-10
                        bg-[var(--color-brand-subtle)]"
                      layoutId="navActiveIndicator"
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 32,
                      }}
                    />
                  )}
                </Link>
              </li>
            ))}
          </ul>
          <LanguageSwitcher />
        </nav>
      </motion.div>
    </header>
  );
}
